/**
 * Custom hook untuk mengambil detail booking berdasarkan ID.
 * 
 * API Endpoint: GET `/api/booking-calendar`
 * API Version: 3.0
 * 
 * @param {string} gte - Tanggal checkin
 * @param {string} lte - Tanggal checkout
 * @param {string} room_id - ID kamar
 * @returns {object} - Return value berupa object dengan key `data`, `isLoading`, dan `error`.
 * @example
 * const { data, isLoading, error } = useAvailability({ gte: '2022-01-01', lte: '2022-01-02' });
 */

import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { DataCalendarResponseSchema } from "../response/DataCalendarResponse";

const API_VERSION = 'v3';

interface DataCalendarProps {
    query?: string;
    gte: string;
    lte: string;
    room_id?: string | null;
    property_id?: string | null;
}

const useDataCalendar = ({ gte, lte, room_id, query, property_id }: DataCalendarProps) => {
    return useQuery({
        queryKey: ['bookings', 'booking', 'calendar', gte, lte, query, room_id, property_id],
        queryFn: async () => {
            try {
                const response = await privateApi.get(`/${API_VERSION}/booking-calendar`, {
                    params: {
                        filter: {
                            ...(room_id !== undefined && { room_id }),
                            ...(property_id !== undefined && { property_id }),
                            date: {
                                gte,
                                lte,
                            }
                        },
                    }
                });

                // Validasi response
                const validationResult = DataCalendarResponseSchema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid Calendar data format");
                }
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch Calendar`, error);
                throw error;
            }
        },
        enabled: !!gte && !!lte && !!property_id,
        retry: import.meta.env.VITE_MAX_RETRY,
        refetchOnWindowFocus: false,
        refetchInterval: 10000,
    });
};

export default useDataCalendar;